import { EmptyTableState } from '@nesso/ui/blocks/table/empty-table-state';
import { Checkbox } from '@nesso/ui/primitives/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@nesso/ui/primitives/table';

import type { UnpaidSessionCharge } from '@/entities/session-charge';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericShortDate } from '@/shared/lib/time-utils';

type Props = {
    unpaid: UnpaidSessionCharge[];
    selectedIds: Set<string>;
    totalCents: number;
    disabled?: boolean;
    onToggleSession: (charge: UnpaidSessionCharge, checked: boolean) => void;
    onToggleAllSessions: (checked: boolean) => void;
};

export function RecordPaymentSheetServices({
    unpaid,
    selectedIds,
    totalCents,
    disabled,
    onToggleSession,
    onToggleAllSessions,
}: Props) {
    const selectedCount = unpaid.filter((charge) => selectedIds.has(charge.id)).length;
    const allSelected = unpaid.length > 0 && selectedCount === unpaid.length;

    return (
        <section className="flex flex-col gap-4 pb-6">
            <h3 className="text-lg font-bold">Select services</h3>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead
                            variant="plain"
                            className="w-11.25 px-2 text-center [&:has([role=checkbox])]:pr-2"
                        >
                            <Checkbox
                                aria-label="Select all sessions"
                                checked={allSelected || (selectedCount > 0 && 'indeterminate')}
                                disabled={disabled || unpaid.length === 0}
                                onCheckedChange={(checked) => onToggleAllSessions(checked === true)}
                            />
                        </TableHead>
                        <TableHead variant="plain">Service</TableHead>
                        <TableHead variant="plain" className="pr-5 text-right">
                            Amount
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="[&_tr:last-child]:border-b">
                    {unpaid.length === 0 ? (
                        <EmptyTableState colSpan={3} message="No unpaid sessions" tone="default" />
                    ) : (
                        unpaid.map((charge) => {
                            const date = formatNumericShortDate(charge.date);

                            return (
                                <TableRow key={charge.id} className="h-18">
                                    <TableCell className="px-2 text-center [&:has([role=checkbox])]:pr-2">
                                        <Checkbox
                                            aria-label={`${date} ${charge.serviceCode}`}
                                            checked={selectedIds.has(charge.id)}
                                            disabled={disabled}
                                            onCheckedChange={(checked) =>
                                                onToggleSession(charge, checked === true)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="font-semibold">
                                                {charge.serviceCode}
                                            </span>
                                            <span className="text-muted-foreground">{date}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="pr-5 text-right">
                                        {formatCurrencyFromCents(charge.balanceCents)}
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
            <div className="mt-2 ml-auto flex w-56.25 items-center justify-between pr-5 text-sm font-bold">
                <span>Total</span>
                <span className="w-17 text-right">{formatCurrencyFromCents(totalCents)}</span>
            </div>
        </section>
    );
}
